import { Link } from "wouter";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/Cards";
import { useSections, useProjectCoverage } from "@/hooks/useSections";
import { useMatrixItems } from "@/hooks/useProjects";
import { computeNextStep, type NextStepResult } from "@/lib/nextStep";
import type { ProjectDetail } from "@/lib/api";

/**
 * The "what should I do next on this bid?" card on the Summary page.
 *
 * Reads project + sections + matrix + coverage and feeds them into the
 * shared `computeNextStep` state machine. The same machine drives the
 * one-line indicator on each Dashboard project card.
 */
export default function NextStepCard({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: string;
}) {
  const sections = useSections(projectId);
  const matrix = useMatrixItems(projectId);
  const coverage = useProjectCoverage(projectId);

  if (sections.isLoading || matrix.isLoading) return null;

  const sectionList = sections.data ?? [];
  const matrixList = matrix.data ?? [];
  const cov = coverage.data;

  const next = computeNextStep({
    projectId,
    hasRfpFile: Boolean(project.rfpFileId),
    hasRawRfpText: Boolean(project.rawRfpText),
    hasSummary: Boolean(project.summary),
    hasFormat: Boolean(project.submissionFormat),
    status: project.status,
    sectionCount: sectionList.length,
    sectionsComplete: sectionList.filter((s) => s.status === "complete").length,
    sectionsMissingProse: sectionList.filter(
      (s) =>
        (s.kind === "prose_ai" || s.kind === "prose_external") &&
        (!s.draftText || s.draftText.trim().length < 20),
    ).length,
    sectionsMissingAsset: sectionList.filter(
      (s) =>
        (s.kind === "designer_asset" ||
          s.kind === "attachment" ||
          s.kind === "pricing" ||
          s.kind === "prose_external") &&
        !s.assetFileId,
    ).length,
    matrixTotal: matrixList.length,
    deliverableTotal: cov?.deliverable ?? 0,
    deliverableCovered: cov?.deliverableCovered ?? 0,
  });

  const Icon = iconForTone(next.tone);
  const colors = colorsForTone(next.tone);

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: colors.bg }}
        >
          <Icon size={16} style={{ color: colors.fg }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] uppercase tracking-wider font-bold"
            style={{ color: "#9CA3AF" }}
          >
            Next step
          </p>
          <h3 className="text-sm font-semibold text-white mt-0.5">{next.title}</h3>
          {next.description && (
            <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
              {next.description}
            </p>
          )}
        </div>
        {next.action && (
          <Link href={next.action.href}>
            <button
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{
                backgroundColor: "#F97316",
                color: "white",
              }}
            >
              {next.action.label}
              <ArrowRight size={12} />
            </button>
          </Link>
        )}
      </div>
    </Card>
  );
}

function iconForTone(tone: NextStepResult["tone"]): React.ElementType {
  switch (tone) {
    case "ok":
      return CheckCircle2;
    case "muted":
      return Loader2;
    default:
      return AlertCircle;
  }
}

function colorsForTone(tone: NextStepResult["tone"]): {
  fg: string;
  bg: string;
} {
  switch (tone) {
    case "ok":
      return { fg: "#22c55e", bg: "rgba(34,197,94,0.1)" };
    case "muted":
      return { fg: "#9CA3AF", bg: "rgba(255,255,255,0.04)" };
    default:
      return { fg: "#F97316", bg: "rgba(249,115,22,0.1)" };
  }
}
